import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Chip
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { SelectChangeEvent } from '@mui/material/Select';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getReservationById, 
  updateReservation,
  checkAvailability,
  findAvailableVessel,
  createReservation
} from '../../firebase/services/reservationService';
import { getActiveVessels, getVesselsByType } from '../../firebase/services/vesselService';
import { Reservation, Vessel, TimeSlot } from '../../models/types';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, CheckCircle as CheckCircleIcon, Warning as WarningIcon } from '@mui/icons-material';
import { createCustomer, Customer, searchCustomers } from '../../firebase/services/customerVesselService';

const ReservationEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  
  // State for the reservation data
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Availability checking state
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [availabilityMessage, setAvailabilityMessage] = useState<string>('');

  // Form state
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [vesselTypes, setVesselTypes] = useState<string[]>([]);
  const [selectedVesselType, setSelectedVesselType] = useState<string>('');
  const [partySize, setPartySize] = useState<number>(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no-show'>('pending');

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Fetch reservation and vessel types in parallel
        const [reservationData, vesselsData] = await Promise.all([
          getReservationById(id),
          getVesselsByType()
        ]);
        
        if (!reservationData) {
          setError('Reservation not found');
          return;
        }
        
        setReservation(reservationData);
        const types = Object.keys(vesselsData);
        setVesselTypes(types);
        setSelectedVesselType(reservationData.vesselType || types[0] || '');
        
        // Populate form fields
        setSelectedDate(parseISO(reservationData.date));
        setStartTime(parseTimeString(reservationData.startTime));
        setEndTime(parseTimeString(reservationData.endTime));
        setPartySize(reservationData.partySize);
        setFirstName(reservationData.customerDetails.firstName);
        setLastName(reservationData.customerDetails.lastName);
        setEmail(reservationData.customerDetails.email);
        setPhone(reservationData.customerDetails.phone);
        setNotes(reservationData.notes || '');
        setStatus(reservationData.status);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load reservation data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  // Check availability when date, time, or vessel type changes
  useEffect(() => {
    if (selectedDate && startTime && endTime && selectedVesselType && reservation) {
      const checkCurrentAvailability = async () => {
        const available = await checkEditAvailability(selectedVesselType, selectedDate, startTime, endTime);
        setIsAvailable(available);
      };
      
      // Debounce the availability check to avoid too many API calls
      const timeoutId = setTimeout(checkCurrentAvailability, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedDate, startTime, endTime, selectedVesselType, reservation]);

  // Helper function to parse time string to Date object
  const parseTimeString = (timeStr: string): Date => {
    const today = new Date();
    const [time, period] = timeStr.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    
    let hour24 = hours;
    if (period === 'PM' && hours !== 12) hour24 += 12;
    if (period === 'AM' && hours === 12) hour24 = 0;
    
    const date = new Date(today);
    date.setHours(hour24, minutes, 0, 0);
    return date;
  };

  // Helper function to format Date object to time string
  const formatTimeString = (date: Date | null): string => {
    if (!date) return '';
    
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Helper to capitalize each word
  const toTitleCase = (str: string) => str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

  // Check availability excluding the current reservation
  const checkEditAvailability = async (
    vesselType: string,
    newDate: Date,
    newStartTime: Date,
    newEndTime: Date
  ): Promise<boolean> => {
    if (!newDate || !newStartTime || !newEndTime || !reservation || !vesselTypes.length) {
      return false;
    }

    try {
      setCheckingAvailability(true);
      setAvailabilityMessage('Checking availability...');

      const dateString = format(newDate, 'yyyy-MM-dd');
      
      // Format times to 24-hour format for API
      const startHour24 = newStartTime.getHours().toString().padStart(2, '0');
      const startMinute = newStartTime.getMinutes().toString().padStart(2, '0');
      const endHour24 = newEndTime.getHours().toString().padStart(2, '0');
      const endMinute = newEndTime.getMinutes().toString().padStart(2, '0');
      
      const timeSlot = `${startHour24}:${startMinute}-${endHour24}:${endMinute}` as TimeSlot;

      // Check if we're editing to the same vessel type, date, and time slot
      const currentDateString = format(parseISO(reservation.date), 'yyyy-MM-dd');
      const isSameType = vesselType === reservation.vesselType;
      const isSameDate = dateString === currentDateString;
      const isSameTimeSlot = timeSlot === reservation.timeSlot;

      // If it's exactly the same reservation (no changes), return true
      if (isSameType && isSameDate && isSameTimeSlot) {
        setAvailabilityMessage('Current reservation time slot');
        return true;
      }

      // Check for available vessel of this type
      const availableVesselId = await findAvailableVessel(
        vesselType,
        reservation.partySize,
        dateString,
        timeSlot
      );

      if (!availableVesselId) {
        setAvailabilityMessage('No vessels of this type are available for the selected time slot');
        return false;
      }

      setAvailabilityMessage('Time slot is available');
      return true;

    } catch (error) {
      console.error('Error checking availability:', error);
      setAvailabilityMessage('Error checking availability');
      return false;
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleSave = async () => {
    if (!selectedDate || !startTime || !endTime) {
      setError('Please fill in all required fields');
      return;
    }

    // Check availability before saving
    if (isAvailable === false) {
      setError('The selected time slot is not available. Please choose a different time or vessel type.');
      return;
    }

    // If availability hasn't been checked yet, check it now
    if (isAvailable === null) {
      const available = reservation
        ? await checkEditAvailability(selectedVesselType, selectedDate, startTime, endTime)
        : true; // For new, skip checkEditAvailability
      if (!available) {
        setError('The selected time slot is not available. Please choose a different time or vessel type.');
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);

      const dateString = format(selectedDate, 'yyyy-MM-dd');
      const startHour24 = startTime.getHours().toString().padStart(2, '0');
      const startMinute = startTime.getMinutes().toString().padStart(2, '0');
      const endHour24 = endTime.getHours().toString().padStart(2, '0');
      const endMinute = endTime.getMinutes().toString().padStart(2, '0');
      const timeSlot = `${startHour24}:${startMinute}-${endHour24}:${endMinute}` as TimeSlot;

      if (id && reservation) {
        // Edit existing reservation
        const availableVesselId = await findAvailableVessel(
          selectedVesselType,
          partySize,
          dateString,
          timeSlot
        );

        if (!availableVesselId) {
          setError('No vessels of this type are available for the selected time slot. Please choose a different time.');
          return;
        }

        const updatedData: Partial<Reservation> = {
          date: dateString,
          startTime: formatTimeString(startTime),
          endTime: formatTimeString(endTime),
          vesselId: availableVesselId,
          vesselType: selectedVesselType,
          timeSlot,
          partySize,
          customerDetails: {
            firstName,
            lastName,
            email,
            phone
          },
          notes,
          status,
          updatedAt: new Date()
        };

        await updateReservation(id, updatedData);
        setSuccess(true);
        setTimeout(() => {
          navigate(`/staff/reservations/${id}`);
        }, 2000);
      } else {
        // Create new reservation
        const availableVesselId = await findAvailableVessel(
          selectedVesselType,
          partySize,
          dateString,
          timeSlot
        );

        if (!availableVesselId) {
          setError('No vessels of this type are available for the selected time slot. Please choose a different time.');
          return;
        }

        // Create or find customer
        let customerId: string;
        try {
          // Try to find existing customer by email
          const existingCustomers = await searchCustomers(email);
          const existingCustomer = existingCustomers.find(
            c => c.email.toLowerCase() === email.toLowerCase()
          );
          
          if (existingCustomer) {
            customerId = existingCustomer.id;
          } else {
            // Create new customer
            const newCustomer = await createCustomer({
              firstName,
              lastName,
              email: email.toLowerCase(),
              phone,
              vessels: []
            });
            customerId = newCustomer.id;
          }
        } catch (err) {
          console.error('Error handling customer:', err);
          setError('Failed to create or find customer record');
          return;
        }

        const newReservation: Omit<Reservation, 'id' | 'createdAt' | 'updatedAt'> = {
          date: dateString,
          startTime: formatTimeString(startTime),
          endTime: formatTimeString(endTime),
          vesselId: availableVesselId,
          vesselType: selectedVesselType,
          vesselName: `${toTitleCase(selectedVesselType)} Rental`,
          timeSlot,
          partySize,
          customerDetails: {
            firstName,
            lastName,
            email,
            phone
          },
          notes,
          status,
          customerId: customerId, // Use the actual customer ID
          originalPrice: 0,
          finalPrice: 0,
          version: 1,
          paymentStatus: 'pending',
          createdBy: userProfile?.id || 'staff',
          locationId: 'mz9knr7tZGItuksXGvjM',
        };

        const created = await createReservation(newReservation);
        setSuccess(true);
        setTimeout(() => {
          navigate(`/staff/reservations/${created.id}`);
        }, 2000);
      }
    } catch (err) {
      console.error('Error saving reservation:', err);
      setError('Failed to save reservation');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/staff/reservations/${id}`);
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !reservation) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
        <Button
          onClick={() => navigate('/staff/dashboard')}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1">
              Edit Reservation
            </Typography>
            <Chip 
              label={reservation?.id || ''} 
              color="primary" 
              variant="outlined"
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Reservation updated successfully! Redirecting...
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Customer Information */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Customer Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Reservation Details */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Reservation Details
                  </Typography>
                  {checkingAvailability && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Checking availability for selected time...
                    </Alert>
                  )}
                  <Grid container spacing={2}>
                                         <Grid item xs={12} sm={6}>
                       <DatePicker
                         label="Date"
                         value={selectedDate}
                         onChange={setSelectedDate}
                         renderInput={(props) => <TextField {...props} fullWidth required />}
                       />
                     </Grid>
                     <Grid item xs={12} sm={3}>
                       <TextField
                         label="Start Time"
                         type="time"
                         value={startTime ? `${startTime.getHours().toString().padStart(2, '0')}:${startTime.getMinutes().toString().padStart(2, '0')}` : ''}
                         onChange={e => {
                           const [hours, minutes] = e.target.value.split(":").map(Number);
                           if (selectedDate) {
                             const newDate = new Date(selectedDate);
                             newDate.setHours(hours, minutes, 0, 0);
                             setStartTime(newDate);
                           } else {
                             const today = new Date();
                             today.setHours(hours, minutes, 0, 0);
                             setStartTime(today);
                           }
                         }}
                         fullWidth
                         required
                         InputLabelProps={{ shrink: true }}
                         inputProps={{ step: 300 }} // 5 min steps
                       />
                     </Grid>
                     <Grid item xs={12} sm={3}>
                       <TextField
                         label="End Time"
                         type="time"
                         value={endTime ? `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}` : ''}
                         onChange={e => {
                           const [hours, minutes] = e.target.value.split(":").map(Number);
                           if (selectedDate) {
                             const newDate = new Date(selectedDate);
                             newDate.setHours(hours, minutes, 0, 0);
                             setEndTime(newDate);
                           } else {
                             const today = new Date();
                             today.setHours(hours, minutes, 0, 0);
                             setEndTime(today);
                           }
                         }}
                         fullWidth
                         required
                         InputLabelProps={{ shrink: true }}
                         inputProps={{ step: 300 }} // 5 min steps
                       />
                     </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Vessel Type</InputLabel>
                        <Select
                          value={selectedVesselType}
                          label="Vessel Type"
                          onChange={(e: SelectChangeEvent) => setSelectedVesselType(e.target.value)}
                          required
                        >
                          {vesselTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                              {toTitleCase(type)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Party Size"
                        type="number"
                        value={partySize}
                        onChange={(e) => setPartySize(parseInt(e.target.value) || 1)}
                        inputProps={{ min: 1, max: 12 }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={status}
                          label="Status"
                          onChange={(e: SelectChangeEvent) => setStatus(e.target.value as any)}
                        >
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="confirmed">Confirmed</MenuItem>
                          <MenuItem value="cancelled">Cancelled</MenuItem>
                          <MenuItem value="completed">Completed</MenuItem>
                          <MenuItem value="no-show">No Show</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                                         <Grid item xs={12}>
                       <TextField
                         fullWidth
                         label="Notes"
                         multiline
                         rows={3}
                         value={notes}
                         onChange={(e) => setNotes(e.target.value)}
                       />
                     </Grid>
                     
                     {/* Availability Status */}
                     <Grid item xs={12}>
                       <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                         {checkingAvailability ? (
                           <>
                             <CircularProgress size={20} sx={{ mr: 1 }} />
                             <Typography variant="body2" color="text.secondary">
                               {availabilityMessage}
                             </Typography>
                           </>
                         ) : isAvailable === true ? (
                           <>
                             <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                             <Typography variant="body2" color="success.main">
                               {availabilityMessage}
                             </Typography>
                           </>
                         ) : isAvailable === false ? (
                           <>
                             <WarningIcon color="error" sx={{ mr: 1 }} />
                             <Typography variant="body2" color="error.main">
                               {availabilityMessage}
                             </Typography>
                           </>
                         ) : null}
                       </Box>
                     </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              startIcon={<ArrowBackIcon />}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || checkingAvailability || isAvailable === false}
              startIcon={<SaveIcon />}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default ReservationEditPage; 